import { ThemeProvider } from "@/context/ThemeContext";
import "@/styles/fonts.css";
import "@/styles/globals.css";
import "react-toastify/dist/ReactToastify.css";

import type { AppProps } from "next/app";

import queryClient from "@/lib/reactQuery";
import { QueryClientProvider } from "@tanstack/react-query";
import { FC, ReactNode, useState } from "react";

import { LoggerComponent } from "@/components/features/unsorted/LoggerComponent";
import { UsageLogger } from "@/components/features/unsorted/UsageLogger";
import { ChartSettingsProvider } from "@/context/ChartSettingsContext";
import { ClientOnly } from "@/utils/ClientOnly";
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  NumberFilterModule,
} from "ag-grid-community";
import { useTheme } from "next-themes";
import { Bounce, ToastContainer } from "react-toastify";

// Register all Community features
ModuleRegistry.registerModules([
  AllCommunityModule,
  NumberFilterModule,
  ClientSideRowModelModule,
]);

const AppProviders: FC<{ children: ReactNode }> = ({ children }) => {
  // so that new query client is not created on every render (useful for hot reaload in local development)
  const [client] = useState(() => queryClient);

  return (
    <QueryClientProvider client={client}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        enableColorScheme
      >
        <ChartSettingsProvider>{children}</ChartSettingsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default function App({ Component, pageProps }: AppProps) {
  const { resolvedTheme } = useTheme();

  return (
    <AppProviders>
      <ClientOnly>
        <LoggerComponent />
        <UsageLogger />
      </ClientOnly>
      <Component {...pageProps} />
      <ToastContainer
        position="bottom-right"
        autoClose={2500}
        hideProgressBar={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        transition={Bounce}
      />
    </AppProviders>
  );
}
